const API_URL ="https://pcoe-api.server.bbiazin.net";
//const API_URL ="http://localhost:3000/";

let logoutCallback = null;
let authErrorCallback = null;
const SESSION_ORG_KEY = "sessaoOrganizacao";

export const setLogoutCallback = (callback) => {
  logoutCallback = callback;
};

export const setAuthErrorCallback = (callback) => {
  authErrorCallback = callback;
};

const notifyAuthError = (type, message) => {
  if (typeof authErrorCallback === "function") {
    authErrorCallback({ type, message });
  }
};

const fetchWithAuth = async (url, options = {}) => {
  const token = localStorage.getItem("token");
  const sessaoOrganizacaoRaw = localStorage.getItem(SESSION_ORG_KEY);
  const sessaoOrganizacao = sessaoOrganizacaoRaw
    ? JSON.parse(sessaoOrganizacaoRaw)
    : null;

  const isFormData = options.body instanceof FormData;
  const headers = {
    ...(isFormData ? {} : { "Content-Type": "application/json" }),
    ...options.headers,
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  if (sessaoOrganizacao?.organizacaoId) {
    headers["X-Organizacao-Id"] = sessaoOrganizacao.organizacaoId;
  }

  try {
    const response = await fetch(`${API_URL}${url}`, {
      ...options,
      headers,
    });

    if (response.status === 403 || response.status === 401) {
      return rejeitarRespostaDeAutenticacao(response);
    }

    return response;
  } catch (error) {
    throw error;
  }
};

const MENSAGEM_SESSAO_EXPIRADA = "Sessão expirada. Faça login novamente.";
const MENSAGEM_SESSAO_INVALIDA = "Sessão inválida. Faça login novamente.";
const MENSAGEM_SEM_ESCOPO = "Sessão sem escopo de organização válido.";
const MENSAGEM_ACESSO_NEGADO = "Acesso negado.";

const criarErroDeAutenticacao = (mensagem, status) => {
  const erro = new Error(mensagem);
  erro.status = status;
  return erro;
};

const mensagemIndicaSessaoExpirada = (mensagem) => {
  return /token\s+(expirado|inv\S*lido)|jwt/i.test(mensagem);
};

const mensagemIndicaEscopoDeOrganizacao = (mensagem) => {
  const normalizada = mensagem.toLowerCase();
  return (
    normalizada.includes("organização") ||
    normalizada.includes("organizacao") ||
    normalizada.includes("escopo")
  );
};

// Lê o corpo de um 401/403 uma única vez e sempre rejeita aqui. Devolver a
// Response já consumida faria o service falhar com "body stream already read".
const rejeitarRespostaDeAutenticacao = async (response) => {
  const status = response.status;
  let mensagem = "";

  try {
    const errorData = await response.json();
    mensagem = errorData?.mensagem || errorData?.message || "";
  } catch (jsonError) {
    if (logoutCallback) {
      logoutCallback();
    }
    notifyAuthError("unauthorized", MENSAGEM_SESSAO_INVALIDA);
    return Promise.reject(
      criarErroDeAutenticacao(MENSAGEM_SESSAO_INVALIDA, status),
    );
  }

  if (mensagemIndicaSessaoExpirada(mensagem)) {
    if (logoutCallback) {
      logoutCallback();
    }
    notifyAuthError("session_expired", MENSAGEM_SESSAO_EXPIRADA);
    return Promise.reject(
      criarErroDeAutenticacao(MENSAGEM_SESSAO_EXPIRADA, status),
    );
  }

  if (mensagemIndicaEscopoDeOrganizacao(mensagem)) {
    const mensagemEscopo = mensagem || MENSAGEM_SEM_ESCOPO;
    notifyAuthError("organization_scope", mensagemEscopo);
    return Promise.reject(criarErroDeAutenticacao(mensagemEscopo, status));
  }

  return Promise.reject(
    criarErroDeAutenticacao(mensagem || MENSAGEM_ACESSO_NEGADO, status),
  );
};

const parseApiErrorMessage = async (response, defaultMessage) => {
  try {
    const error = await response.json();
    const detalhes = error?.erros
      ? Object.values(error.erros).filter(Boolean).join(" ")
      : "";
    return [error?.mensagem || error?.message || defaultMessage, detalhes]
      .filter(Boolean)
      .join(": ");
  } catch {
    return defaultMessage;
  }
};

// ==================== AUTH SERVICE ====================
const authService = {
  login: async (email, password) => {
    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email, password: password }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.mensagem || "Falha na autenticação");
      }

      const data = await response.json();

      if (data.token && data.usuario) {
        localStorage.setItem("token", data.token);

        const usuario = {
          id: data.usuario.id,
          nome: data.usuario.nome,
          email: data.usuario.email,
          permissao: data.usuario.permissao,
          role: data.usuario.permissao,
          authorities: [{ authority: `ROLE_${data.usuario.permissao}` }],
          ativo: true,
          createdAt: data.usuario.createdAt,
        };

        localStorage.setItem("userData", JSON.stringify(usuario));
        localStorage.setItem(
          SESSION_ORG_KEY,
          JSON.stringify(data.sessaoOrganizacao || null),
        );

        localStorage.getItem("userData");

        return {
          token: data.token,
          user: usuario,
          sessaoOrganizacao: data.sessaoOrganizacao || null,
        };
      }

      throw new Error("Token não recebido");
    } catch (error) {
      throw error;
    }
  },

  getCurrentUser: () => {
    const userData = localStorage.getItem("userData");
    const token = localStorage.getItem("token");

    // Se não tem token ou token está vazio, limpar dados
    if (!token || token.trim() === "") {
      localStorage.removeItem("userData");
      localStorage.removeItem("token");
      return null;
    }

    if (userData) {
      try {
        return JSON.parse(userData);
      } catch (error) {
        // Se dados do usuário estão corrompidos, limpar tudo
        localStorage.removeItem("userData");
        localStorage.removeItem("token");
        return null;
      }
    }
    return null;
  },

  getSessaoOrganizacao: () => {
    const raw = localStorage.getItem(SESSION_ORG_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      localStorage.removeItem(SESSION_ORG_KEY);
      return null;
    }
  },

  setSessaoOrganizacao: (sessaoOrganizacao) => {
    localStorage.setItem(SESSION_ORG_KEY, JSON.stringify(sessaoOrganizacao));
  },

  definirOrganizacaoAtiva: (organizacaoId) => {
    const sessao = authService.getSessaoOrganizacao();
    if (!sessao) return null;

    const organizacao = (sessao.organizacoes || []).find(
      (o) => o.id === organizacaoId,
    );
    if (!organizacao) {
      throw new Error("organização não permitida para este usuário.");
    }

    const atualizada = {
      ...sessao,
      organizacaoId,
      permissaoOrganizacao: organizacao.permissao,
      ultimaOrganizacaoSelecionada: organizacaoId,
    };
    authService.setSessaoOrganizacao(atualizada);
    return atualizada;
  },

  trocarOrganizacao: async (organizacaoId) => {
    const response = await fetchWithAuth("/auth/trocar-organizacao", {
      method: "POST",
      body: JSON.stringify({ organizacaoId }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Erro ao trocar organização");
    }

    const data = await response.json();
    if (!data?.token || !data?.sessaoOrganizacao || !data?.usuario) {
      throw new Error("Resposta inválida ao trocar organização.");
    }

    const usuario = {
      id: data.usuario.id,
      nome: data.usuario.nome,
      email: data.usuario.email,
      permissao: data.usuario.permissao,
      role: data.usuario.permissao,
      authorities: [{ authority: `ROLE_${data.usuario.permissao}` }],
      ativo: true,
      createdAt: data.usuario.createdAt,
    };

    localStorage.setItem("token", data.token);
    localStorage.setItem("userData", JSON.stringify(usuario));
    authService.setSessaoOrganizacao(data.sessaoOrganizacao);

    return {
      token: data.token,
      user: usuario,
      sessaoOrganizacao: data.sessaoOrganizacao,
    };
  },

  hasRole: (user, role, sessaoOrganizacao) => {
    if (!user) {
      return false;
    }

    const roleNormalizada = role.toUpperCase();
    const isRoleGlobal = roleNormalizada === "SUPER_ADMIN";

    if (isRoleGlobal) {
      if (user.role) {
        return user.role.toUpperCase() === roleNormalizada;
      }

      if (user.permissao) {
        return user.permissao.toUpperCase() === roleNormalizada;
      }

      if (Array.isArray(user.authorities)) {
        return user.authorities.some((auth) => {
          if (typeof auth === "string") {
            return auth === `ROLE_${roleNormalizada}`;
          }
          return auth?.authority === `ROLE_${roleNormalizada}`;
        });
      }

      return false;
    }

    if (roleNormalizada === "ADMIN") {
      if (user.role && user.role.toUpperCase() === "SUPER_ADMIN") {
        return true;
      }

      if (user.permissao && user.permissao.toUpperCase() === "SUPER_ADMIN") {
        return true;
      }

      if (Array.isArray(user.authorities)) {
        const hasSuperAdminAuthority = user.authorities.some((auth) => {
          if (typeof auth === "string") {
            return auth === "ROLE_SUPER_ADMIN";
          }
          return auth?.authority === "ROLE_SUPER_ADMIN";
        });

        if (hasSuperAdminAuthority) {
          return true;
        }
      }
    }

    if (sessaoOrganizacao?.permissaoOrganizacao) {
      return (
        sessaoOrganizacao.permissaoOrganizacao.toUpperCase() === roleNormalizada
      );
    }

    if (user.role) {
      const hasRole = `ROLE_${user.role}` === `ROLE_${roleNormalizada}`;
      return hasRole;
    }

    if (user.permissao) {
      const hasPermission = user.permissao === roleNormalizada;
      return hasPermission;
    }

    if (user.authorities) {
      if (Array.isArray(user.authorities)) {
        const hasAuth = user.authorities.some((auth) => {
          if (typeof auth === "string") {
            return auth === `ROLE_${roleNormalizada}`;
          }
          return (
            auth &&
            auth.authority &&
            auth.authority === `ROLE_${roleNormalizada}`
          );
        });
        return hasAuth;
      }
    }
    return false;
  },

  logout: () => {
    localStorage.removeItem("token");
    localStorage.removeItem("userData");
    localStorage.removeItem(SESSION_ORG_KEY);
  },
};

// ==================== CAIXA SERVICE ====================
// Lê a resposta das rotas de pedido do agente Sisodonto. O status HTTP vai no
// erro: 503 = robô offline, 409 = já existe integração em andamento.
const lerRespostaPedidoSisodonto = async (response, mensagemPadrao) => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    const err = new Error(error.mensagem || mensagemPadrao);
    err.status = response.status;
    throw err;
  }
  return response.json();
};

const caixaService = {
  // Listar caixas para usuÃ¡rio logado
  listarCaixas: async () => {
    const response = await fetchWithAuth("/caixa");
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Falha ao obter caixas");
    }
    return response.json();
  },

  // Listar caixa por data para usuÃ¡rio logado
  listarCaixaPorData: async (data) => {
    const response = await fetchWithAuth(`/caixa/data?data=${data}`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Falha ao obter caixa por data");
    }
    return response.json();
  },

  // Listar caixas por perÃ­odo para usuÃ¡rio logado
  listarCaixasPorPeriodo: async (dataInicial, dataFinal) => {
    const response = await fetchWithAuth(
      `/caixa/datas?dataInicial=${dataInicial}&dataFinal=${dataFinal}`,
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Falha ao obter caixas por perÃ­odo");
    }
    return response.json();
  },

  // Listar caixas abertos para usuÃ¡rio logado
  listarCaixasAbertos: async () => {
    const response = await fetchWithAuth("/caixa/aberto");
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Falha ao obter caixas abertos");
    }
    return response.json();
  },

  // Listar caixa por ID
  listarCaixaPorId: async (id) => {
    const response = await fetchWithAuth(`/caixa/${id}`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Falha ao obter caixa");
    }
    return response.json();
  },

  // Abrir caixa
  abrirCaixa: async (dataCaixa) => {
    const url = dataCaixa
      ? `/caixa/abrir?dataCaixa=${dataCaixa}`
      : "/caixa/abrir";
    const response = await fetchWithAuth(url, {
      method: "POST",
    });

    if (!response.ok) {
      // Extrair mensagem de erro do JSON
      let errorMessage = `HTTP ${response.status}`;

      try {
        const errorData = await response.json();
        errorMessage = errorData.mensagem || errorMessage;
      } catch (jsonError) {}

      throw new Error(errorMessage);
    }

    // Verificar se hÃ¡ conteÃºdo JSON na resposta de sucesso
    const contentType = response.headers.get("content-type");
    if (!contentType?.includes("application/json")) {
      return {};
    }

    const text = await response.text();
    if (!text || text.trim() === "") {
      return {};
    }

    return JSON.parse(text);
  },

  // Adicionar movimento ao caixa
  adicionarMovimento: async (id, movimentoData) => {
    const response = await fetchWithAuth(`/caixa/movimento/${id}`, {
      method: "POST",
      body: JSON.stringify(movimentoData),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Erro ao adicionar movimento");
    }
    return response.json();
  },

  importarArquivo: async (arquivo, tipoCaixaId) => {
    const formData = new FormData();
    formData.append("arquivo", arquivo);

    const response = await fetchWithAuth(
      `/caixa/importar?tipoCaixaId=${encodeURIComponent(tipoCaixaId)}`,
      {
        method: "POST",
        body: formData,
      },
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Erro ao importar caixas");
    }

    return response.json();
  },

  // Integração Sisodonto por agente local (robô no PC do usuário). O ADMIN
  // cria um pedido para uma data; o robô o recebe, extrai e devolve; o
  // backend cria os caixas quando o pedido é consultado já extraído.
  criarPedidoSisodonto: async (dataInicial, dataFinal) => {
    const response = await fetchWithAuth("/caixa/integracao/sisodonto/pedidos", {
      method: "POST",
      body: JSON.stringify({ dataInicial, dataFinal }),
    });
    return lerRespostaPedidoSisodonto(
      response,
      "Erro ao solicitar a integração com o Sisodonto",
    );
  },

  obterPedidoSisodonto: async (id) => {
    const response = await fetchWithAuth(
      `/caixa/integracao/sisodonto/pedidos/${encodeURIComponent(id)}`,
    );
    return lerRespostaPedidoSisodonto(
      response,
      "Erro ao consultar a integração com o Sisodonto",
    );
  },

  // Fechar caixa
  fecharCaixa: async (id) => {
    const response = await fetchWithAuth(`/caixa/fechar/${id}`, {
      method: "POST",
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Erro ao fechar caixa");
    }
    return response.json();
  },

  // Deletar caixa
  deletarCaixa: async (id) => {
    const response = await fetchWithAuth(`/caixa/${id}`, {
      method: "DELETE",
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Erro ao deletar caixa");
    }
    return true;
  },

  // Gerar PDF do envelope do caixa
  gerarPdfEnvelopeCaixa: async (id) => {
    const response = await fetchWithAuth(`/caixa/${id}/envelope/pdf`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Erro ao gerar PDF do envelope");
    }
    return response.blob();
  },
};

// ==================== CAIXA GERAL SERVICE ====================
const caixaGeralService = {
  // Listar todos os caixas (Admin)
  listarTodosCaixas: async () => {
    const response = await fetchWithAuth("/caixa-geral");
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Falha ao obter caixas gerais");
    }
    return response.json();
  },

  // Listar caixa geral por usuÃ¡rio (Admin)
  listarCaixaGeralPorUsuario: async (usuario) => {
    const response = await fetchWithAuth(
      `/caixa-geral/usuario?usuario=${usuario}`,
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Falha ao obter caixas por usuÃ¡rio");
    }
    return response.json();
  },

  // Listar caixa geral por periodo e por um usuario especifico
  listarCaixaGeralPorPeriodoEoUsuario: async (
    usuarioId,
    dataInicial,
    dataFinal,
  ) => {
    const params = new URLSearchParams({
      usuarioId,
      dataInicial,
      dataFinal,
    });

    const response = await fetchWithAuth(
      `/caixa-geral/usuario-datas?${params.toString()}`,
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.mensagem || "Falha ao obter caixas por usuÃ¡rio e periodo",
      );
    }
    return response.json();
  },

  // Listar caixa geral por data (Admin)
  listarCaixaGeralPorData: async (data) => {
    const response = await fetchWithAuth(`/caixa-geral/data?data=${data}`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Falha ao obter caixa geral por data");
    }
    return response.json();
  },

  // Listar caixa geral entre datas (Admin)
  listarCaixaGeralEntreDatas: async (dataInicial, dataFinal) => {
    const response = await fetchWithAuth(
      `/caixa-geral/datas?dataInicial=${dataInicial}&dataFinal=${dataFinal}`,
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.mensagem || "Falha ao obter caixas gerais entre datas",
      );
    }
    return response.json();
  },

  // Marcar/Desmarcar caixas selecionados (Admin)
  checarCaixas: async (caixas) => {
    const response = await fetchWithAuth("/caixa-geral", {
      method: "POST",
      body: JSON.stringify(caixas),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.mensagem || "Falha ao processar checagem de caixas",
      );
    }
    return;
  },

  // Marcar todos os caixas informados como checados (Admin)
  checarTodosCaixas: async (caixas) => {
    const response = await fetchWithAuth("/caixa-geral/todos", {
      method: "POST",
      body: JSON.stringify(caixas),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.mensagem || "Falha ao processar checagem de todos os caixas",
      );
    }
    return;
  },
};

// ==================== APURACAO MENSAL FUNDOS SERVICE ====================
const apuracaoMensalFundosService = {
  // Apurar distribuiÃ§Ã£o de fundos por competÃªncia (Admin)
  apurarPorCompetencia: async (competencia) => {
    const params = new URLSearchParams({
      competencia,
    });

    const response = await fetchWithAuth(
      `/apuracao-mensal-fundos?${params.toString()}`,
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.mensagem || "Falha ao obter apuraÃ§Ã£o mensal de fundos",
      );
    }

    return response.json();
  },

  // Calcular distribuiÃ§Ã£o de fundos por competÃªncia (Admin)
  calcularPorCompetencia: async (competencia) => {
    const params = new URLSearchParams({
      competencia,
    });

    const response = await fetchWithAuth(
      `/apuracao-mensal-fundos/calcular?${params.toString()}`,
      {
        method: "POST",
      },
    );

    if (!response.ok) {
      let errorMessage = "Falha ao calcular apuraÃ§Ã£o mensal de fundos";

      try {
        const error = await response.json();
        errorMessage = error.mensagem || errorMessage;
      } catch (jsonError) {}

      throw new Error(errorMessage);
    }

    return;
  },
};

// ==================== DASHBOARD DESPESAS SERVICE ====================
const dashboardDespesasService = {
  // Apurar despesas por intervalo de competÃªncias (Admin)
  apurarPorCompetencias: async (competenciaInicio, competenciaFim) => {
    const params = new URLSearchParams({
      competenciaInicio,
      competenciaFim,
    });

    const response = await fetchWithAuth(
      `/dashboard-despesas?${params.toString()}`,
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Falha ao obter dashboard de despesas");
    }

    return response.json();
  },
};

// ==================== DASHBOARD FINANCEIRO SERVICE ====================
const dashboardFinanceiroService = {
  // Obter crescimento do caixa por competÃªncia (Admin)
  crescimentoCaixaPorCompetencia: async (competencia) => {
    const params = new URLSearchParams({
      competencia,
    });

    const response = await fetchWithAuth(
      `/dashboard-financeiro/crescimento-caixa?${params.toString()}`,
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Falha ao obter crescimento de caixa");
    }

    return response.json();
  },
};

// ==================== COMPARATIVO FINANCEIRO SERVICE ====================
const comparativoFinanceiroService = {
  // Obter comparativo financeiro por quantidade de meses e tipo de caixa (Admin)
  obterComparativo: async (qtdMeses, tipoCaixaId) => {
    const params = new URLSearchParams({
      qtdMeses: String(qtdMeses),
    });

    // Sem tipo selecionado significa "Todos os tipos": o parâmetro é omitido.
    if (tipoCaixaId) {
      params.set("tipoCaixaId", tipoCaixaId);
    }

    const response = await fetchWithAuth(
      `/comparativo-financeiro?${params.toString()}`,
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.mensagem || "Falha ao obter comparativo financeiro",
      );
    }

    return response.json();
  },
};

// ==================== TIPO CAIXA SERVICE ====================
const tipoCaixaService = {
  // Listar Tipo Caixa
  listarTipoCaixa: async (nome) => {
    const url = nome ? `/tipo-caixa?nome=${nome}` : "/tipo-caixa";

    const response = await fetchWithAuth(url);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Falha ao obter Tipo Caixa");
    }
    return response.json();
  },

  // Listar TipoCaixa por ID
  listarTipoCaixaPorId: async (id) => {
    const response = await fetchWithAuth(`/tipo-caixa/${id}`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Falha ao obter Tipo de Caixa");
    }
    return response.json();
  },

  // Cadastrar Tipo de Caixa
  cadastrarTipoCaixa: async (tipoCaixaData) => {
    const response = await fetchWithAuth("/tipo-caixa", {
      method: "POST",
      body: JSON.stringify(tipoCaixaData),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Erro ao cadastrar Tipo de Caixa");
    }
    return response.json();
  },

  // Atualizar Tipo de Caixa
  atualizarTipoCaixa: async (id, tipoCaixaData) => {
    const response = await fetchWithAuth(`/tipo-caixa/${id}`, {
      method: "PUT",
      body: JSON.stringify(tipoCaixaData),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Erro ao atualizar Tipo de Caixa");
    }
    return response.json();
  },

  // Deletar Tipo de Caixa
  deletarTipoCaixa: async (id) => {
    const response = await fetchWithAuth(`/tipo-caixa/${id}`, {
      method: "DELETE",
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Erro ao deletar Tipo de Caixa");
    }
    return true;
  },
};

// ==================== CONFIGURAÇÃO INTEGRAÇÃO SISODONTO SERVICE ====================
// Configuração única por organização (tipo de caixa vinculado a cada
// módulo do Sisodonto + usuário dono dos Caixa criados pela
// integração automática), em vez de repetida em cada Configuração Caixa Sisodonto.
const configuracaoIntegracaoSisodontoService = {
  buscar: async () => {
    const response = await fetchWithAuth("/configuracao-integracao-sisodonto");
    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.mensagem || "Falha ao obter configuração da integração Sisodonto",
      );
    }
    return response.json();
  },

  atualizar: async (configuracaoData) => {
    const response = await fetchWithAuth("/configuracao-integracao-sisodonto", {
      method: "PUT",
      body: JSON.stringify(configuracaoData),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.mensagem ||
          "Erro ao atualizar configuração da integração Sisodonto",
      );
    }
    return response.json();
  },
};

// Robô local do Sisodonto (agente no PC do usuário): estado de conexão e
// credencial de acesso. O token gerado só existe na resposta de gerar().
const agenteSisodontoService = {
  status: async () => {
    const response = await fetchWithAuth(
      "/caixa/integracao/sisodonto/agente/status",
    );
    return lerRespostaPedidoSisodonto(
      response,
      "Erro ao consultar o estado do robô",
    );
  },

  gerarCredencial: async () => {
    const response = await fetchWithAuth(
      "/caixa/integracao/sisodonto/agente/credencial",
      { method: "POST" },
    );
    return lerRespostaPedidoSisodonto(
      response,
      "Erro ao gerar a credencial do robô",
    );
  },

  revogarCredencial: async () => {
    const response = await fetchWithAuth(
      "/caixa/integracao/sisodonto/agente/credencial",
      { method: "DELETE" },
    );
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.mensagem || "Erro ao revogar a credencial do robô");
    }
  },
};

// ==================== FUNDO RESERVA SERVICE ====================
const fundoReservaService = {
  // Listar fundos de reserva
  listarFundosReserva: async () => {
    const response = await fetchWithAuth("/fundo-reserva");
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Falha ao obter fundos de reserva");
    }
    return response.json();
  },

  // Listar fundo de reserva por ID
  listarFundoReservaPorId: async (id) => {
    const response = await fetchWithAuth(`/fundo-reserva/${id}`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Falha ao obter fundo de reserva");
    }
    return response.json();
  },

  // Cadastrar fundo de reserva
  cadastrarFundoReserva: async (fundoReservaData) => {
    const response = await fetchWithAuth("/fundo-reserva", {
      method: "POST",
      body: JSON.stringify(fundoReservaData),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Erro ao cadastrar fundo de reserva");
    }
    return response.json();
  },

  // Atualizar fundo de reserva
  atualizarFundoReserva: async (id, fundoReservaData) => {
    const response = await fetchWithAuth(`/fundo-reserva/${id}`, {
      method: "PUT",
      body: JSON.stringify(fundoReservaData),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Erro ao atualizar fundo de reserva");
    }
    return response.json();
  },

  // Deletar fundo de reserva
  deletarFundoReserva: async (id) => {
    const response = await fetchWithAuth(`/fundo-reserva/${id}`, {
      method: "DELETE",
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Erro ao excluir fundo de reserva");
    }
    return true;
  },
};

// ==================== MOVIMENTO FUNDO RESERVA SERVICE ====================
const movimentoFundoReservaService = {
  listarPorFundo: async (fundoReservaId) => {
    const query = fundoReservaId ? `?fundoReservaId=${fundoReservaId}` : "";
    const response = await fetchWithAuth(`/mvto-fundo-reserva${query}`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.mensagem || "Falha ao obter movimentacoes do fundo de reserva",
      );
    }
    return response.json();
  },

  listarPorCompetencia: async (competencia, fundoReservaId) => {
    const params = new URLSearchParams();
    if (competencia) {
      params.set("competencia", competencia);
    }
    if (fundoReservaId) {
      params.set("fundoReservaId", fundoReservaId);
    }

    const query = params.toString();
    const response = await fetchWithAuth(
      `/mvto-fundo-reserva/competencia${query ? `?${query}` : ""}`,
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.mensagem || "Falha ao obter movimentacoes por competencia",
      );
    }

    return response.json();
  },

  listarExtratoPorCompetencia: async (competencia) => {
    const params = new URLSearchParams({
      competencia,
    });

    const response = await fetchWithAuth(
      `/mvto-fundo-reserva/extrato?${params.toString()}`,
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.mensagem || "Falha ao obter extrato do fundo de reserva",
      );
    }

    return response.json();
  },

  listarPorPeriodo: async (fundoReservaId, dataInicio, dataFim) => {
    const params = new URLSearchParams();
    if (fundoReservaId) {
      params.set("fundoReservaId", fundoReservaId);
    }
    if (dataInicio) {
      params.set("dataInicio", dataInicio);
    }
    if (dataFim) {
      params.set("dataFim", dataFim);
    }

    const query = params.toString();
    const response = await fetchWithAuth(
      `/mvto-fundo-reserva/search${query ? `?${query}` : ""}`,
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.mensagem || "Falha ao obter movimentacoes por periodo",
      );
    }

    return response.json();
  },

  buscarPorId: async (id) => {
    const response = await fetchWithAuth(`/mvto-fundo-reserva/${id}`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.mensagem || "Falha ao obter movimentacao do fundo de reserva",
      );
    }
    return response.json();
  },

  criar: async (dto) => {
    const response = await fetchWithAuth(`/mvto-fundo-reserva`, {
      method: "POST",
      body: JSON.stringify(dto),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.mensagem || "Erro ao criar movimentacao do fundo de reserva",
      );
    }
    return response.json();
  },

  atualizar: async (id, dto) => {
    const response = await fetchWithAuth(`/mvto-fundo-reserva/${id}`, {
      method: "PUT",
      body: JSON.stringify(dto),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.mensagem || "Erro ao atualizar movimentacao do fundo de reserva",
      );
    }
    return response.json();
  },

  deletar: async (id) => {
    const response = await fetchWithAuth(`/mvto-fundo-reserva/${id}`, {
      method: "DELETE",
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.mensagem || "Erro ao excluir movimentacao do fundo de reserva",
      );
    }
    return true;
  },
};

// ==================== ESPECIALIDADE SERVICE ====================
const especialidadeService = {
  // Listar especialidades
  listarEspecialidades: async (nome) => {
    const url = nome ? `/especialidade?nome=${nome}` : "/especialidade";
    const response = await fetchWithAuth(url);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Falha ao obter especialidades");
    }
    return response.json();
  },

  // Listar especialidade por ID
  listarEspecialidadePorId: async (id) => {
    const response = await fetchWithAuth(`/especialidade/${id}`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Falha ao obter especialidade");
    }
    return response.json();
  },

  // Cadastrar especialidade
  cadastrarEspecialidade: async (especialidadeData) => {
    const response = await fetchWithAuth("/especialidade", {
      method: "POST",
      body: JSON.stringify(especialidadeData),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Erro ao cadastrar especialidade");
    }
    return response.json();
  },

  // Atualizar especialidade
  atualizarEspecialidade: async (id, especialidadeData) => {
    const response = await fetchWithAuth(`/especialidade/${id}`, {
      method: "PUT",
      body: JSON.stringify(especialidadeData),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Erro ao atualizar especialidade");
    }
    return response.json();
  },

  // Deletar especialidade
  deletarEspecialidade: async (id) => {
    const response = await fetchWithAuth(`/especialidade/${id}`, {
      method: "DELETE",
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Erro ao deletar especialidade");
    }
    return true;
  },
};

// ==================== FOLHA PAGAMENTO SERVICE ====================
const folhaPagamentoService = {
  // Listar folhas de pagamento
  listarFolhaPagamentos: async (id) => {
    const url = id
      ? `/folha-pagamento?profissionalId=${id}`
      : "/folha-pagamento";
    const response = await fetchWithAuth(url);

    if (!response.ok) {
      if (response.status === 400) {
        const error = await response.json();
        if (
          error.mensagem &&
          error.mensagem.toLowerCase().includes("nenhum registro")
        ) {
          return []; // Retorna lista vazia ao invÃ©s de erro
        }
      }
      const error = await response.json();
      throw new Error(error.mensagem || "Falha ao obter folhas de pagamento");
    }
    return response.json();
  },

  // Listar resumo mensal da folha de pagamento
  listarFolhaPagamentoMensal: async (competencia, profissionalId) => {
    const params = new URLSearchParams({ competencia });

    if (profissionalId) {
      params.append("profissionalId", profissionalId);
    }

    const response = await fetchWithAuth(
      `/folha-pagamento/mensal?${params.toString()}`,
    );

    if (!response.ok) {
      if (response.status === 400) {
        const error = await response.json();
        if (
          error.mensagem &&
          error.mensagem.toLowerCase().includes("nenhum registro")
        ) {
          return []; // Retorna lista vazia ao invÃ©s de erro
        }
      }
      const error = await response.json();
      throw new Error(error.mensagem || "Falha ao obter folha de pagamento");
    }
    return response.json();
  },

  // Listar folha de pagamento por ID
  listarFolhaPagamentoPorId: async (id) => {
    const response = await fetchWithAuth(`/folha-pagamento/${id}`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Falha ao obter folha de pagamento");
    }
    return response.json();
  },

  // Listar folha de pagamento por periodo
  listarFolhaPagamentoPorPeriodo: async (dataInicio, dataFinal) => {
    const response = await fetchWithAuth(
      `/folha-pagamento/periodo?dataInicio=${dataInicio}&dataFinal=${dataFinal}`,
    );

    if (!response.ok) {
      if (response.status === 400) {
        const error = await response.json();
        if (
          error.mensagem &&
          error.mensagem.toLowerCase().includes("nenhum registro")
        ) {
          return []; // Retorna lista vazia ao invÃ©s de erro
        }
      }
      const error = await response.json();
      throw new Error(error.mensagem || "Falha ao obter folha de pagamento");
    }
    return response.json();
  },
  listarFolhaPagamentoPorProfissionalIdEoPeriodo: async (
    profissionalId,
    dataInicio,
    dataFinal,
  ) => {
    const response = await fetchWithAuth(
      `/folha-pagamento/profissional-periodo?profissionalId=${profissionalId}&dataInicio=${dataInicio}&dataFinal=${dataFinal}`,
    );

    if (!response.ok) {
      if (response.status === 400) {
        const error = await response.json();
        if (
          error.mensagem &&
          error.mensagem.toLowerCase().includes("nenhum registro")
        ) {
          return []; // Retorna lista vazia ao invÃ©s de erro
        }
      }
      const error = await response.json();
      throw new Error(error.mensagem || "Falha ao obter folha de pagamento");
    }
    return response.json();
  },

  // Cadastrar folha de pagamento
  cadastrarFolhaPagamento: async (folhaData) => {
    const response = await fetchWithAuth("/folha-pagamento", {
      method: "POST",
      body: JSON.stringify(folhaData),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Erro ao cadastrar folha de pagamento");
    }
    return response.json();
  },

  // Atualizar folha de pagamento
  atualizarFolhaPagamento: async (id, folhaData) => {
    const response = await fetchWithAuth(`/folha-pagamento/${id}`, {
      method: "PUT",
      body: JSON.stringify(folhaData),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Erro ao atualizar folha de pagamento");
    }
    return response.json();
  },

  // Deletar folha de pagamento
  deletarFolhaPagamento: async (id) => {
    const response = await fetchWithAuth(`/folha-pagamento/${id}`, {
      method: "DELETE",
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Erro ao deletar folha de pagamento");
    }
    return true;
  },

  // Emitir apenas um PDF da folha de pagamento
  gerarPdfFolhaPagamento: async (id) => {
    const response = await fetchWithAuth(`/folha-pagamento/${id}/pdf`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.mensagem || "Erro ao gerar PDF da folha de pagamento",
      );
    }
    return response.blob();
  },

  gerarVariosPdfFolhaPagamento: async (ids) => {
    const response = await fetchWithAuth("/folha-pagamento/recibos-ids/pdf", {
      method: "POST",
      body: JSON.stringify(ids),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.mensagem || "Erro ao gerar PDF da folha de pagamento",
      );
    }
    return response.blob();
  },
};

// ==================== FORMA PAGAMENTO SERVICE ====================
const formaPagamentoService = {
  // Listar formas de pagamento
  listarFormasPagamento: async (nome) => {
    const url = nome ? `/forma-pagamento?nome=${nome}` : "/forma-pagamento";
    const response = await fetchWithAuth(url);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Falha ao obter formas de pagamento");
    }
    return response.json();
  },

  // Listar forma de pagamento por ID
  listarFormaPagamentoPorId: async (id) => {
    const response = await fetchWithAuth(`/forma-pagamento/${id}`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Falha ao obter forma de pagamento");
    }
    return response.json();
  },

  // Cadastrar forma de pagamento
  cadastrarFormaPagamento: async (formaData) => {
    const response = await fetchWithAuth("/forma-pagamento", {
      method: "POST",
      body: JSON.stringify(formaData),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Erro ao cadastrar forma de pagamento");
    }
    return response.json();
  },

  // Atualizar forma de pagamento
  atualizarFormaPagamento: async (id, formaData) => {
    const response = await fetchWithAuth(`/forma-pagamento/${id}`, {
      method: "PUT",
      body: JSON.stringify(formaData),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Erro ao atualizar forma de pagamento");
    }
    return response.json();
  },

  // Deletar forma de pagamento
  deletarFormaPagamento: async (id) => {
    const response = await fetchWithAuth(`/forma-pagamento/${id}`, {
      method: "DELETE",
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Erro ao deletar forma de pagamento");
    }
    return true;
  },
};

// ==================== PROFISSIONAL SERVICE ====================
const profissionalService = {
  // Listar profissionais
  listarProfissionais: async (nome) => {
    const url = nome ? `/profissional?nome=${nome}` : "/profissional";
    const response = await fetchWithAuth(url);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Falha ao obter profissionais");
    }
    return response.json();
  },

  // Listar profissional por ID
  listarProfissionalPorId: async (id) => {
    const response = await fetchWithAuth(`/profissional/${id}`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Falha ao obter profissional");
    }
    return response.json();
  },

  // Cadastrar profissional
  cadastrarProfissional: async (profissionalData) => {
    const response = await fetchWithAuth("/profissional", {
      method: "POST",
      body: JSON.stringify(profissionalData),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Erro ao cadastrar profissional");
    }
    return response.json();
  },

  // Atualizar profissional
  atualizarProfissional: async (id, profissionalData) => {
    const response = await fetchWithAuth(`/profissional/${id}`, {
      method: "PUT",
      body: JSON.stringify(profissionalData),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Erro ao atualizar profissional");
    }
    return response.json();
  },

  // Deletar profissional
  deletarProfissional: async (id) => {
    const response = await fetchWithAuth(`/profissional/${id}`, {
      method: "DELETE",
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Erro ao deletar profissional");
    }
    return true;
  },
};

// ==================== SOLICITACAO REABERTURA CAIXA SERVICE ====================
const solicitacaoReaberturaCaixaService = {
  // Listar requisiÃ§Ãµes de reabertura (para solicitante)
  listarRequisicaoReabertura: async (status) => {
    const response = await fetchWithAuth(
      `/reabertura-caixa/minhas-solicitacao?status=${status}`,
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.mensagem || "Falha ao obter requisiÃ§Ãµes de reabertura",
      );
    }
    return response.json();
  },

  // Listar respostas de reabertura (para admin)
  listarSolicitacaoReabertura: async (status) => {
    const response = await fetchWithAuth(
      `/reabertura-caixa/solicitacao?status=${status}`,
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.mensagem || "Falha ao obter respostas de reabertura",
      );
    }
    return response.json();
  },

  // Solicitar reabertura de caixa
  solicitarReaberturaCaixa: async (solicitacaoData) => {
    const response = await fetchWithAuth("/reabertura-caixa", {
      method: "POST",
      body: JSON.stringify(solicitacaoData),
    });
    if (!response.ok) {
      let errorMessage = "Erro ao solicitar reabertura de caixa";
      try {
        const errorBody = await response.text(); // Tenta pegar como texto
        if (errorBody) {
          const errorJson = JSON.parse(errorBody); // Tenta converter
          errorMessage = errorJson.mensagem || errorMessage;
        }
      } catch (e) {}

      throw new Error(errorMessage);
    }

    // Se a resposta tiver corpo (ex: JSON), tente parsear
    try {
      return await response.json();
    } catch {
      return null; // Ou simplesmente `return`, se nÃ£o espera resposta
    }
  },

  // Atualizar resposta de reabertura (Admin)
  atualizarSolicitacaoReabertura: async (id, respostaData) => {
    const response = await fetchWithAuth(`/reabertura-caixa/${id}`, {
      method: "POST",
      body: JSON.stringify(respostaData),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.mensagem || "Erro ao atualizar resposta de reabertura",
      );
    }
    return response.json();
  },
};

// ==================== SOLICITACAO ABERTURA CAIXA SERVICE ====================
const solicitacaoAberturaCaixaService = {
  // Listar requisiÃ§Ãµes de abertura (para solicitante)
  listarRequisicaoAbertura: async (status) => {
    const response = await fetchWithAuth(
      `/abertura-caixa/minhas-solicitacao?status=${status}`,
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.mensagem || "Falha ao obter requisiÃ§Ãµes de abertura",
      );
    }
    return response.json();
  },

  // Listar respostas de abertura (para admin)
  listarSolicitacaoAbertura: async (status) => {
    const response = await fetchWithAuth(
      `/abertura-caixa/solicitacao?status=${status}`,
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Falha ao obter respostas de abertura");
    }
    return response.json();
  },

  // Solicitar abertura de caixa
  solicitarAberturaCaixa: async (solicitacaoAberturaCaixa) => {
    const response = await fetchWithAuth(`/abertura-caixa`, {
      method: "POST",
      body: JSON.stringify(solicitacaoAberturaCaixa),
    });

    if (!response.ok) {
      let errorMessage = "Erro ao solicitar abertura de caixa";

      try {
        const errorBody = await response.text(); // Tenta pegar como texto
        if (errorBody) {
          const errorJson = JSON.parse(errorBody); // Tenta converter
          errorMessage = errorJson.mensagem || errorMessage;
        }
      } catch (e) {}

      throw new Error(errorMessage);
    }

    // Se a resposta tiver corpo (ex: JSON), tente parsear
    try {
      return await response.json();
    } catch {
      return null; // Ou simplesmente `return`, se nÃ£o espera resposta
    }
  },

  // Atualizar resposta de abertura (Admin)
  atualizarSolicitacaoAbertura: async (id, respostaData) => {
    const response = await fetchWithAuth(`/abertura-caixa/${id}`, {
      method: "POST",
      body: JSON.stringify(respostaData),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.mensagem || "Erro ao atualizar resposta de abertura",
      );
    }
    return response.json();
  },
};

// ==================== USUARIO SERVICE ====================
const usuarioService = {
  listarUsuarios: async (nome) => {
    const url = nome ? `/usuario?nome=${nome}` : "/usuario";
    const response = await fetchWithAuth(url);
    if (!response.ok) {
      throw new Error(
        await parseApiErrorMessage(response, "Falha ao obter usuarios"),
      );
    }
    return response.json();
  },

  listarUsuariosAtivos: async () => {
    const url = "/usuario/ativo";
    const response = await fetchWithAuth(url);
    if (!response.ok) {
      throw new Error(
        await parseApiErrorMessage(response, "Falha ao obter usuarios"),
      );
    }
    return response.json();
  },

  listarUsuarioPorId: async (id) => {
    const response = await fetchWithAuth(`/usuario/${id}`);
    if (!response.ok) {
      throw new Error(
        await parseApiErrorMessage(response, "Falha ao obter usuario"),
      );
    }
    return response.json();
  },

  listarOrganizacoesPermitidas: async () => {
    const response = await fetchWithAuth("/usuario/organizacoes-permitidas");
    if (!response.ok) {
      const error = new Error(
        await parseApiErrorMessage(
          response,
          "Erro ao carregar organizacoes permitidas",
        ),
      );
      error.status = response.status;
      throw error;
    }
    return response.json();
  },

  listarOrganizacoesPermitidasNoContexto: async (organizacaoId) => {
    const response = await fetchWithAuth(
      `/usuario/organizacoes-permitidas/${organizacaoId}`,
    );
    if (!response.ok) {
      const error = new Error(
        await parseApiErrorMessage(
          response,
          "Erro ao validar organizacao no contexto atual",
        ),
      );
      error.status = response.status;
      throw error;
    }
    return response.json();
  },

  cadastrarUsuario: async (usuarioData) => {
    const response = await fetchWithAuth("/usuario", {
      method: "POST",
      body: JSON.stringify(usuarioData),
    });
    if (!response.ok) {
      throw new Error(
        await parseApiErrorMessage(response, "Erro ao cadastrar usuario"),
      );
    }
    return response.json();
  },

  atualizarUsuario: async (id, usuarioData) => {
    const response = await fetchWithAuth(`/usuario/${id}`, {
      method: "PUT",
      body: JSON.stringify(usuarioData),
    });
    if (!response.ok) {
      throw new Error(
        await parseApiErrorMessage(response, "Erro ao atualizar usuario"),
      );
    }
    return response.json();
  },

  solicitarRedefinicaoSenha: async (emailDTO) => {
    const link = `${window.location.origin}/redefinir-senha`;
    emailDTO.link = link;
    const response = await fetchWithAuth(
      "/usuario/solicitar-redefinicao-senha",
      {
        method: "POST",
        body: JSON.stringify(emailDTO),
      },
    );
    if (!response.ok) {
      throw new Error(
        await parseApiErrorMessage(response, "Erro ao atualizar usuario"),
      );
    }
    return response.json();
  },

  redefinirSenha: async (redefinirSenhaDTO) => {
    const response = await fetchWithAuth("/usuario/redefinir-senha", {
      method: "PATCH",
      body: JSON.stringify(redefinirSenhaDTO),
    });
    if (!response.ok) {
      throw new Error(
        await parseApiErrorMessage(response, "Erro ao atualizar usuario"),
      );
    }
    return response.json();
  },

  deletarUsuario: async (id) => {
    const response = await fetchWithAuth(`/usuario/${id}`, {
      method: "DELETE",
    });
    if (!response.ok) {
      throw new Error(
        await parseApiErrorMessage(response, "Erro ao deletar usuario"),
      );
    }
    return true;
  },
};

// ==================== IMPEDIMENTO SERVICE ====================
const impedimentoService = {
  // Listar datas com impedimentos (admin)
  listaImpedimentos: async (nome) => {
    const url = nome ? `/impedimento?nome=${nome}` : "/impedimento";
    const response = await fetchWithAuth(url);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.json().mensagem || "Falha ao obter datas com impedimentos",
      );
    }
    return response.json();
  },

  // cadastrar impedimentos (admin)
  cadastrarImpedimento: async (impedimento) => {
    const response = await fetchWithAuth(`/impedimento`, {
      method: "POST",
      body: JSON.stringify(impedimento),
    });

    if (!response.ok) {
      let errorMessage = "Erro ao cadastrar impedimento";

      try {
        const errorBody = await response.text(); // Tenta pegar como texto
        if (errorBody) {
          const errorJson = JSON.parse(errorBody); // Tenta converter
          errorMessage = errorJson.mensagem || errorMessage;
        }
      } catch (e) {}

      throw new Error(errorMessage);
    }

    // Se a resposta tiver corpo (ex: JSON), tente parsear
    try {
      return await response.json();
    } catch {
      return null; // Ou simplesmente `return`, se nÃ£o espera resposta
    }
  },

  // Atualizar impedimentos (Admin)
  atualizarImpedimentos: async (id, impedimento) => {
    const response = await fetchWithAuth(`/impedimento/${id}`, {
      method: "PUT",
      body: JSON.stringify(impedimento),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Erro ao atualizar impedimentos");
    }
    return response.json();
  },

  deletarImpedimentos: async (id) => {
    const response = await fetchWithAuth(`/impedimento/${id}`, {
      method: "DELETE",
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Erro ao deletar impedimento");
    }
    return true;
  },
};

// ==================== USUARIO DATAS COM IMPEDIMENTO SERVICE ====================
const datasComImpedimentoService = {
  // Listar datas com impedimentos (admin)
  listaDatasComImpedimentos: async (nome) => {
    const url = nome
      ? `/data-impedido-lancar-caixa-diario?nome=${nome}`
      : "/data-impedido-lancar-caixa-diario";
    const response = await fetchWithAuth(url);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.json().mensagem || "Falha ao obter datas com impedimentos",
      );
    }
    return response.json();
  },

  // cadastrar datas com impedimentos (admin)
  cadastrarDatasComImpedimentos: async (dataImpedidoLancarCaixaDiario) => {
    const response = await fetchWithAuth(`/data-impedido-lancar-caixa-diario`, {
      method: "POST",
      body: JSON.stringify(dataImpedidoLancarCaixaDiario),
    });
    if (!response.ok) {
      const errorText = await response.text();
      try {
        const error = JSON.parse(errorText);
        throw new Error(error.mensagem || "Erro ao cadastrar impedimento");
      } catch (e) {
        throw new Error("Erro ao processar resposta do servidor");
      }
    }
    return await response.json();
  },

  // Atualizar datas com impedimentos (Admin)
  atualizarDatasComImpedimentos: async (id, dataAtualizado) => {
    const response = await fetchWithAuth(
      `/data-impedido-lancar-caixa-diario/${id}`,
      {
        method: "PUT",
        body: JSON.stringify(dataAtualizado),
      },
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.mensagem || "Erro ao atualizar datas com impedimentos",
      );
    }
    return response.json();
  },

  deletarDatasComImpedimentos: async (id) => {
    const response = await fetchWithAuth(
      `/data-impedido-lancar-caixa-diario/${id}`,
      {
        method: "DELETE",
      },
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.mensagem || "Erro ao deletar datas com impedimentos",
      );
    }
    return true;
  },
};

// ==================== Grupo ======================

const grupoService = {
  // Listar grupos (admin)
  listarGrupo: async (nome) => {
    const url = nome ? `/grupo?nome=${nome}` : "/grupo";
    const response = await fetchWithAuth(url);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.json().mensagem || "Falha ao obter grupos");
    }
    return response.json();
  },

  // cadastrar grupo (admin)
  cadastrarGrupo: async (grupo) => {
    const response = await fetchWithAuth(`/grupo`, {
      method: "POST",
      body: JSON.stringify(grupo),
    });
    if (!response.ok) {
      const errorText = await response.text();
      try {
        const error = JSON.parse(errorText);
        throw new Error(error.mensagem || "Erro ao cadastrar grupo");
      } catch (e) {
        throw new Error("Erro ao processar resposta do servidor");
      }
    }
    return await response.json();
  },

  // Atualizar grupo (Admin)
  atualizarGrupo: async (id, grupoAtualizado) => {
    const response = await fetchWithAuth(`/grupo/${id}`, {
      method: "PUT",
      body: JSON.stringify(grupoAtualizado),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Erro ao atualizar grupo");
    }
    return response.json();
  },

  deletarGrupo: async (id) => {
    const response = await fetchWithAuth(`/grupo/${id}`, {
      method: "DELETE",
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Erro ao deletar grupo");
    }
    return true;
  },
};

// ==================== Organizacao ======================
const organizacaoService = {
  listarOrganizacoes: async (nome) => {
    const query = nome ? `?nome=${encodeURIComponent(nome)}` : "";
    const response = await fetchWithAuth(`/organizacoes${query}`);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Erro ao listar organizações");
    }

    return response.json();
  },

  listarOrganizacaoPorId: async (id) => {
    const response = await fetchWithAuth(`/organizacoes/${id}`);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Erro ao buscar organização");
    }

    return response.json();
  },

  cadastrarOrganizacao: async (organizacao) => {
    const response = await fetchWithAuth("/organizacoes", {
      method: "POST",
      body: JSON.stringify(organizacao),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Erro ao cadastrar organização");
    }

    return response.json();
  },

  atualizarOrganizacao: async (id, organizacao) => {
    const response = await fetchWithAuth(`/organizacoes/${id}`, {
      method: "PUT",
      body: JSON.stringify(organizacao),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Erro ao atualizar organização");
    }

    return response.json();
  },

  deletarOrganizacao: async (id) => {
    const response = await fetchWithAuth(`/organizacoes/${id}`, {
      method: "DELETE",
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Erro ao excluir organização");
    }

    return true;
  },
};
// ==================== Sub_Grupo ==================
const subGrupoService = {
  // Listar subgrupos (admin)
  listarSubGrupo: async (nome) => {
    const url = nome ? `/subgrupo?nome=${nome}` : "/subgrupo";
    const response = await fetchWithAuth(url);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.json().mensagem || "Falha ao obter subgrupos");
    }
    return response.json();
  },

  listarSubGrupoPorGrupo: async (grupo) => {
    const url = `/subgrupo?search=${grupo}`;
    const response = await fetchWithAuth(url);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.json().mensagem || "Falha ao obter subgrupos");
    }
    return response.json();
  },

  // cadastrar subgrupos (admin)
  cadastrarSubGrupo: async (subgrupo) => {
    const response = await fetchWithAuth(`/subgrupo`, {
      method: "POST",
      body: JSON.stringify(subgrupo),
    });
    if (!response.ok) {
      const errorText = await response.text();
      try {
        const error = JSON.parse(errorText);
        throw new Error(error.mensagem || "Erro ao cadastrar subgrupo");
      } catch (e) {
        throw new Error("Erro ao processar resposta do servidor");
      }
    }
    return await response.json();
  },

  // Atualizar subgrupos (Admin)
  atualizarSubGrupo: async (id, subgrupo) => {
    const response = await fetchWithAuth(`/subgrupo/${id}`, {
      method: "PUT",
      body: JSON.stringify(subgrupo),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Erro ao atualizar subgrupo");
    }
    return response.json();
  },

  deletarSubGrupo: async (id) => {
    const response = await fetchWithAuth(`/subgrupo/${id}`, {
      method: "DELETE",
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Erro ao deletar subgrupos");
    }
    return true;
  },
};
// ==================== Despesa ====================
const despesaService = {
  // Listar despesas (admin)
  listarDespesas: async (filtros = {}) => {
    const params = new URLSearchParams();

    if (filtros.descricao) {
      params.set("descricao", filtros.descricao);
    }

    if (filtros.situacao) {
      params.set("situacao", filtros.situacao);
    }

    if (
      filtros.valorMin !== "" &&
      filtros.valorMin !== null &&
      filtros.valorMin !== undefined
    ) {
      params.set("valorMin", filtros.valorMin);
    }

    if (
      filtros.valorMax !== "" &&
      filtros.valorMax !== null &&
      filtros.valorMax !== undefined
    ) {
      params.set("valorMax", filtros.valorMax);
    }

    if (filtros.campoData) {
      params.set("campoData", filtros.campoData);
    }

    if (filtros.dataDe) {
      params.set("dataDe", filtros.dataDe);
    }

    if (filtros.dataAte) {
      params.set("dataAte", filtros.dataAte);
    }

    if (filtros.grupoId) {
      params.set("grupoId", filtros.grupoId);
    }

    if (filtros.subGrupoId) {
      params.set("subGrupoId", filtros.subGrupoId);
    }

    const query = params.toString();
    const url = query ? `/despesa?${query}` : "/despesa";
    const response = await fetchWithAuth(url);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Falha ao obter despesas");
    }
    return response.json();
  },

  // cadastrar despesas (admin)
  cadastrarDespesas: async (despesa) => {
    const response = await fetchWithAuth(`/despesa`, {
      method: "POST",
      body: JSON.stringify(despesa),
    });
    if (!response.ok) {
      throw new Error(
        await parseApiErrorMessage(response, "Erro ao cadastrar despesa"),
      );
    }
    return await response.json();
  },

  // Atualizar despesas (Admin)
  atualizarDespesas: async (id, despesaAtualizada) => {
    const response = await fetchWithAuth(`/despesa/${id}`, {
      method: "PUT",
      body: JSON.stringify(despesaAtualizada),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Erro ao atualizar despesa");
    }
    return response.json();
  },

  lancarPagamentoDespesas: async (id, despesa) => {
    const response = await fetchWithAuth(`/despesa/${id}`, {
      method: "PATCH",
      body: JSON.stringify(despesa),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Erro ao lanÃ§ar pagamento da despesa");
    }
    return response.json();
  },

  apagarPagamento: async (id) => {
    const response = await fetchWithAuth(`/despesa/apagar-pagamento/${id}`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Erro ao apagar pagamento da despesa");
    }
    return true;
  },

  deletarDespesas: async (id) => {
    const response = await fetchWithAuth(`/despesa/${id}`, {
      method: "DELETE",
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Erro ao deletar despesas");
    }
    return true;
  },

  deletarDespesasComParcelaRef: async (parcelaRef) => {
    const response = await fetchWithAuth(`/despesa/parcela/${parcelaRef}`, {
      method: "DELETE",
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.mensagem || "Erro ao deletar despesas com parcela referenciada",
      );
    }
    return true;
  },
};

// ==================== MAQUINA CARTAO ====================
const maquinaCartaoService = {
  listar: async (nome = "") => {
    const params = new URLSearchParams();
    if (nome) {
      params.set("nome", nome);
    }
    const query = params.toString();
    const response = await fetchWithAuth(
      `/maquina-cartao${query ? `?${query}` : ""}`,
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Erro ao listar maquinas de cartao");
    }
    return response.json();
  },

  buscarPorId: async (id) => {
    const response = await fetchWithAuth(`/maquina-cartao/${id}`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Erro ao buscar maquina de cartao");
    }
    return response.json();
  },

  criar: async (data) => {
    const response = await fetchWithAuth(`/maquina-cartao`, {
      method: "POST",
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Erro ao criar maquina de cartao");
    }
    return response.json();
  },

  atualizar: async (id, data) => {
    const response = await fetchWithAuth(`/maquina-cartao/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Erro ao atualizar maquina de cartao");
    }
    return response.json();
  },

  deletar: async (id) => {
    const response = await fetchWithAuth(`/maquina-cartao/${id}`, {
      method: "DELETE",
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Erro ao deletar maquina de cartao");
    }
    return true;
  },
};

// ==================== TAXA MAQUINA CARTAO ====================
const taxaMaquinaCartaoService = {
  listar: async (filtros = {}) => {
    const params = new URLSearchParams();
    if (filtros.maquinaCartaoId) {
      params.set("maquinaCartaoId", filtros.maquinaCartaoId);
    }
    if (filtros.mesInicio) {
      params.set("mesInicio", filtros.mesInicio);
    }
    if (filtros.mesFim) {
      params.set("mesFim", filtros.mesFim);
    }

    const query = params.toString();
    const response = await fetchWithAuth(
      `/taxa-maquina-cartao${query ? `?${query}` : ""}`,
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.mensagem || "Erro ao listar taxas de maquina de cartao",
      );
    }
    return response.json();
  },

  buscarPorId: async (id) => {
    const response = await fetchWithAuth(`/taxa-maquina-cartao/${id}`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.mensagem || "Erro ao buscar taxa de maquina de cartao",
      );
    }
    return response.json();
  },

  criar: async (data) => {
    const response = await fetchWithAuth(`/taxa-maquina-cartao`, {
      method: "POST",
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.mensagem || "Erro ao criar taxa de maquina de cartao",
      );
    }
    return response.json();
  },

  atualizar: async (id, data) => {
    const response = await fetchWithAuth(`/taxa-maquina-cartao/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.mensagem || "Erro ao atualizar taxa de maquina de cartao",
      );
    }
    return response.json();
  },

  deletar: async (id) => {
    const response = await fetchWithAuth(`/taxa-maquina-cartao/${id}`, {
      method: "DELETE",
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.mensagem || "Erro ao deletar taxa de maquina de cartao",
      );
    }
    return true;
  },
};

// ==================== SOCIO ====================
const socioService = {
  listar: async () => {
    const response = await fetchWithAuth(`/socios`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Erro ao listar socios");
    }
    return response.json();
  },

  buscarPorId: async (id) => {
    const response = await fetchWithAuth(`/socios/${id}`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Erro ao buscar socio");
    }
    return response.json();
  },

  cadastrar: async (data) => {
    const response = await fetchWithAuth(`/socios`, {
      method: "POST",
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Erro ao cadastrar socio");
    }
    return response.json();
  },

  atualizar: async (id, data) => {
    const response = await fetchWithAuth(`/socios/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Erro ao atualizar socio");
    }
    return response.json();
  },

  deletar: async (id) => {
    const response = await fetchWithAuth(`/socios/${id}`, {
      method: "DELETE",
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Erro ao deletar socio");
    }
    return true;
  },
};

// ==================== CONTA BANCARIA SERVICE ====================
const contaBancariaService = {
  listar: async () => {
    const response = await fetchWithAuth("/conta-bancaria");
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Erro ao listar contas bancÃ¡rias");
    }
    return response.json();
  },

  criar: async (dto) => {
    const response = await fetchWithAuth("/conta-bancaria", {
      method: "POST",
      body: JSON.stringify(dto),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Erro ao criar conta bancÃ¡ria");
    }
    return response.json();
  },

  atualizar: async (id, dto) => {
    const response = await fetchWithAuth(`/conta-bancaria/${id}`, {
      method: "PUT",
      body: JSON.stringify(dto),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Erro ao atualizar conta bancÃ¡ria");
    }
    return response.json();
  },

  deletar: async (id) => {
    const response = await fetchWithAuth(`/conta-bancaria/${id}`, {
      method: "DELETE",
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.mensagem || "Erro ao deletar conta bancÃ¡ria");
    }
    return true;
  },
};

// ==================== CONTA BANCARIA VALOR SERVICE ====================
const contaBancariaValorService = {
  listar: async (contaBancariaId) => {
    const query = contaBancariaId ? `?contaBancariaId=${contaBancariaId}` : "";
    const response = await fetchWithAuth(`/conta-bancaria-valor${query}`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.mensagem || "Erro ao listar movimentacoes da conta bancÃ¡ria",
      );
    }
    return response.json();
  },

  listarPorCompetencia: async (competencia) => {
    const response = await fetchWithAuth(
      `/conta-bancaria-valor/competencia?competencia=${encodeURIComponent(competencia)}`,
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.mensagem || "Erro ao listar movimentacoes por competÃªncia",
      );
    }
    return response.json();
  },

  buscarPorId: async (id) => {
    const response = await fetchWithAuth(`/conta-bancaria-valor/${id}`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.mensagem || "Erro ao buscar movimentacao da conta bancÃ¡ria",
      );
    }
    return response.json();
  },

  criar: async (dto) => {
    const response = await fetchWithAuth("/conta-bancaria-valor", {
      method: "POST",
      body: JSON.stringify(dto),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.mensagem || "Erro ao criar movimentacao da conta bancÃ¡ria",
      );
    }
    return response.json();
  },

  atualizar: async (id, dto) => {
    const response = await fetchWithAuth(`/conta-bancaria-valor/${id}`, {
      method: "PUT",
      body: JSON.stringify(dto),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.mensagem || "Erro ao atualizar movimentacao da conta bancÃ¡ria",
      );
    }
    return response.json();
  },

  deletar: async (id) => {
    const response = await fetchWithAuth(`/conta-bancaria-valor/${id}`, {
      method: "DELETE",
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.mensagem || "Erro ao deletar movimentacao da conta bancÃ¡ria",
      );
    }
    return true;
  },
};

// ==================== Fornecedor ====================
const fornecedorService = {
  listar: async ({ busca = "", situacao = "TODOS" } = {}) => {
    const params = new URLSearchParams({ busca, situacao });
    const response = await fetchWithAuth(`/fornecedores?${params.toString()}`);
    if (!response.ok)
      throw new Error(
        await parseApiErrorMessage(response, "Erro ao listar fornecedores"),
      );
    return response.json();
  },
  criar: async (dados) => {
    const response = await fetchWithAuth("/fornecedores", {
      method: "POST",
      body: JSON.stringify(dados),
    });
    if (!response.ok)
      throw new Error(
        await parseApiErrorMessage(response, "Erro ao cadastrar fornecedor"),
      );
    return response.json();
  },
  atualizar: async (id, dados) => {
    const response = await fetchWithAuth(`/fornecedores/${id}`, {
      method: "PUT",
      body: JSON.stringify(dados),
    });
    if (!response.ok)
      throw new Error(
        await parseApiErrorMessage(response, "Erro ao atualizar fornecedor"),
      );
    return response.json();
  },
  alterarSituacao: async (id, ativo) => {
    const response = await fetchWithAuth(`/fornecedores/${id}/situacao`, {
      method: "PATCH",
      body: JSON.stringify({ ativo }),
    });
    if (!response.ok)
      throw new Error(
        await parseApiErrorMessage(
          response,
          "Erro ao alterar situação do fornecedor",
        ),
      );
    return response.json();
  },
  excluir: async (id) => {
    const response = await fetchWithAuth(`/fornecedores/${id}`, {
      method: "DELETE",
    });
    if (!response.ok)
      throw new Error(
        await parseApiErrorMessage(response, "Erro ao excluir fornecedor"),
      );
  },
};

// ==================== Unidade de Controle ====================
const unidadeControleService = {
  listar: async ({ busca = "", situacao = "TODOS" } = {}) => {
    const response = await fetchWithAuth(
      `/unidades-controle?${new URLSearchParams({ busca, situacao })}`,
    );
    if (!response.ok)
      throw new Error(
        await parseApiErrorMessage(
          response,
          "Erro ao listar unidades de controle",
        ),
      );
    return response.json();
  },
  criar: async (dados) => {
    const response = await fetchWithAuth("/unidades-controle", {
      method: "POST",
      body: JSON.stringify(dados),
    });
    if (!response.ok)
      throw new Error(
        await parseApiErrorMessage(
          response,
          "Erro ao cadastrar unidade de controle",
        ),
      );
    return response.json();
  },
  atualizar: async (id, dados) => {
    const response = await fetchWithAuth(`/unidades-controle/${id}`, {
      method: "PUT",
      body: JSON.stringify(dados),
    });
    if (!response.ok)
      throw new Error(
        await parseApiErrorMessage(
          response,
          "Erro ao atualizar unidade de controle",
        ),
      );
    return response.json();
  },
  alterarSituacao: async (id, ativo) => {
    const response = await fetchWithAuth(`/unidades-controle/${id}/situacao`, {
      method: "PATCH",
      body: JSON.stringify({ ativo }),
    });
    if (!response.ok)
      throw new Error(
        await parseApiErrorMessage(
          response,
          "Erro ao alterar situação da unidade",
        ),
      );
    return response.json();
  },
  excluir: async (id) => {
    const response = await fetchWithAuth(`/unidades-controle/${id}`, {
      method: "DELETE",
    });
    if (!response.ok)
      throw new Error(
        await parseApiErrorMessage(
          response,
          "Erro ao excluir unidade de controle",
        ),
      );
  },
};

const produtoService = {
  listar: async ({
    busca = "",
    situacao = "TODOS",
    pagina = 0,
    tamanho = 100,
  } = {}) => {
    const r = await fetchWithAuth(
      `/produtos?${new URLSearchParams({ busca, situacao, pagina, tamanho })}`,
    );
    if (!r.ok)
      throw new Error(await parseApiErrorMessage(r, "Erro ao listar produtos"));
    return r.json();
  },
  listarPagina: async ({
    busca = "",
    situacao = "TODOS",
    pagina = 0,
    tamanho = 50,
  } = {}) => {
    const r = await fetchWithAuth(
      `/produtos/pagina?${new URLSearchParams({ busca, situacao, pagina, tamanho })}`,
    );
    if (!r.ok)
      throw new Error(await parseApiErrorMessage(r, "Erro ao listar produtos"));
    return r.json();
  },
  similaridade: async (dados) => {
    const r = await fetchWithAuth("/produtos/similaridade", {
      method: "POST",
      body: JSON.stringify(dados),
    });
    if (!r.ok)
      throw new Error(
        await parseApiErrorMessage(r, "Erro ao consultar produtos semelhantes"),
      );
    return r.json();
  },
  criar: async (dados) => {
    const r = await fetchWithAuth("/produtos", {
      method: "POST",
      body: JSON.stringify(dados),
    });
    if (!r.ok)
      throw new Error(
        await parseApiErrorMessage(r, "Erro ao cadastrar produto"),
      );
    return r.json();
  },
  atualizar: async (id, dados) => {
    const r = await fetchWithAuth(`/produtos/${id}`, {
      method: "PUT",
      body: JSON.stringify(dados),
    });
    if (!r.ok)
      throw new Error(
        await parseApiErrorMessage(r, "Erro ao atualizar produto"),
      );
    return r.json();
  },
  alterarSituacao: async (id, ativo) => {
    const r = await fetchWithAuth(`/produtos/${id}/situacao`, {
      method: "PATCH",
      body: JSON.stringify({ ativo }),
    });
    if (!r.ok)
      throw new Error(
        await parseApiErrorMessage(r, "Erro ao alterar situação do produto"),
      );
    return r.json();
  },
  excluir: async (id) => {
    const r = await fetchWithAuth(`/produtos/${id}`, { method: "DELETE" });
    if (!r.ok)
      throw new Error(await parseApiErrorMessage(r, "Erro ao excluir produto"));
  },
};

const pedidoCompraService = {
  atual: async () => {
    const r = await fetchWithAuth("/pedidos-compra/atual");
    if (!r.ok)
      throw new Error(
        await parseApiErrorMessage(r, "Erro ao carregar lista de compras"),
      );
    return r.json();
  },
  historico: async () => {
    const r = await fetchWithAuth("/pedidos-compra/historico");
    if (!r.ok)
      throw new Error(
        await parseApiErrorMessage(r, "Erro ao carregar histórico"),
      );
    return r.json();
  },
  buscarProdutos: async (busca = "") => {
    const r = await fetchWithAuth(
      `/pedidos-compra/produtos/busca?${new URLSearchParams({ busca })}`,
    );
    if (!r.ok)
      throw new Error(await parseApiErrorMessage(r, "Erro ao buscar produtos"));
    return r.json();
  },
  buscarFornecedores: async (busca = "") => {
    const r = await fetchWithAuth(
      `/pedidos-compra/fornecedores/busca?${new URLSearchParams({ busca })}`,
    );
    if (!r.ok)
      throw new Error(
        await parseApiErrorMessage(r, "Erro ao buscar fornecedores"),
      );
    return r.json();
  },
  adicionarProdutos: async (dados) => {
    const r = await fetchWithAuth("/pedidos-compra/atual/itens/produtos", {
      method: "POST",
      body: JSON.stringify(dados),
    });
    if (!r.ok)
      throw new Error(
        await parseApiErrorMessage(r, "Erro ao adicionar produtos"),
      );
    return r.json();
  },
  adicionarLivre: async (dados) => {
    const r = await fetchWithAuth("/pedidos-compra/atual/itens/livres", {
      method: "POST",
      body: JSON.stringify(dados),
    });
    if (!r.ok)
      throw new Error(await parseApiErrorMessage(r, "Erro ao adicionar item"));
    return r.json();
  },
  alterarTitulo: async (id, dados) => {
    const r = await fetchWithAuth(`/pedidos-compra/${id}/titulo`, {
      method: "PATCH",
      body: JSON.stringify(dados),
    });
    if (!r.ok)
      throw new Error(
        await parseApiErrorMessage(r, "Erro ao alterar título da lista"),
      );
    return r.json();
  },
  alterarFornecedor: async (id, dados) => {
    const r = await fetchWithAuth(`/pedidos-compra/${id}/fornecedor`, {
      method: "PATCH",
      body: JSON.stringify(dados),
    });
    if (!r.ok)
      throw new Error(
        await parseApiErrorMessage(r, "Erro ao alterar fornecedor"),
      );
    return r.json();
  },
  alterarQuantidade: async (id, itemId, dados) => {
    const r = await fetchWithAuth(
      `/pedidos-compra/${id}/itens/${itemId}/quantidade`,
      { method: "PATCH", body: JSON.stringify(dados) },
    );
    if (!r.ok)
      throw new Error(
        await parseApiErrorMessage(r, "Erro ao alterar quantidade"),
      );
    return r.json();
  },
  converterItemLivreEmNovoProduto: async (id, itemId, dados) => {
    const r = await fetchWithAuth(
      `/pedidos-compra/${id}/itens/${itemId}/novo-produto`,
      { method: "PATCH", body: JSON.stringify(dados) },
    );
    if (!r.ok)
      throw new Error(
        await parseApiErrorMessage(r, "Erro ao atualizar item livre"),
      );
    return r.json();
  },
  vincularProduto: async (id, itemId, dados) => {
    const r = await fetchWithAuth(
      `/pedidos-compra/${id}/itens/${itemId}/produto`,
      { method: "PATCH", body: JSON.stringify(dados) },
    );
    if (!r.ok)
      throw new Error(
        await parseApiErrorMessage(r, "Erro ao vincular produto"),
      );
    return r.json();
  },
  removerItem: async (id, itemId, dados) => {
    const r = await fetchWithAuth(`/pedidos-compra/${id}/itens/${itemId}`, {
      method: "DELETE",
      body: JSON.stringify(dados),
    });
    if (!r.ok)
      throw new Error(await parseApiErrorMessage(r, "Erro ao remover item"));
  },
  finalizar: async (id, dados) => {
    const r = await fetchWithAuth(`/pedidos-compra/${id}/finalizar`, {
      method: "POST",
      body: JSON.stringify(dados),
    });
    if (!r.ok)
      throw new Error(await parseApiErrorMessage(r, "Erro ao finalizar lista"));
    return r.json();
  },
  gerarPdf: async (id) => {
    const r = await fetchWithAuth(`/pedidos-compra/${id}/pdf`);
    if (!r.ok)
      throw new Error(
        await parseApiErrorMessage(r, "Erro ao gerar PDF da lista"),
      );
    return {
      blob: await r.blob(),
      filename: (r.headers.get("content-disposition") || "").match(
        /filename="?([^";]+)"?/i,
      )?.[1],
    };
  },
};

const entradaManualService = {
  pedidos: async (busca = "", fornecedorId = "", notaId = "") => {
    const params = new URLSearchParams({ busca });
    if (fornecedorId) params.set("fornecedorId", fornecedorId);
    if (notaId) params.set("notaId", notaId);
    const r = await fetchWithAuth(
      `/entradas-manuais/pedidos?${params}`,
    );
    if (!r.ok)
      throw new Error(await parseApiErrorMessage(r, "Erro ao buscar pedidos"));
    return r.json();
  },
  historico: async () => {
    const r = await fetchWithAuth("/entradas-manuais/historico");
    if (!r.ok)
      throw new Error(
        await parseApiErrorMessage(r, "Erro ao buscar histórico"),
      );
    return r.json();
  },
  saldo: async (id) => {
    const r = await fetchWithAuth(`/entradas-manuais/produtos/${id}/saldo`);
    if (!r.ok)
      throw new Error(await parseApiErrorMessage(r, "Erro ao consultar saldo"));
    return r.json();
  },
  registrar: async (dados) => {
    const r = await fetchWithAuth("/entradas-manuais", {
      method: "POST",
      body: JSON.stringify(dados),
    });
    if (!r.ok)
      throw new Error(
        await parseApiErrorMessage(r, "Erro ao registrar entrada"),
      );
    return r.json();
  },
  registrarLote: async (dados) => {
    const r = await fetchWithAuth("/entradas-manuais/lote", {
      method: "POST",
      body: JSON.stringify(dados),
    });
    if (!r.ok)
      throw new Error(
        await parseApiErrorMessage(r, "Erro ao registrar entradas"),
      );
    return r.json();
  },
  registrarAvulsa: async (dados) => {
    const r = await fetchWithAuth("/entradas-manuais/avulsas", {
      method: "POST",
      body: JSON.stringify(dados),
    });
    if (!r.ok)
      throw new Error(
        await parseApiErrorMessage(r, "Erro ao registrar entrada avulsa"),
      );
    return r.json();
  },
};
const entradaProdutoService = {
  historico: async (params = {}) => {
    const r = await fetchWithAuth(
      `/entradas-produtos/historico?${new URLSearchParams(params)}`,
    );
    if (!r.ok)
      throw new Error(
        await parseApiErrorMessage(r, "Erro ao carregar histórico de entradas"),
      );
    return r.json();
  },
};
const saidaProdutoService = {
  produtos: async (busca = "") => {
    const r = await fetchWithAuth(
      `/saidas-produtos/produtos?${new URLSearchParams({ busca })}`,
    );
    if (!r.ok)
      throw new Error(
        await parseApiErrorMessage(r, "Erro ao buscar produtos para saída"),
      );
    return r.json();
  },
  saldo: async (id) => {
    const r = await fetchWithAuth(`/saidas-produtos/${id}/saldo`);
    if (!r.ok)
      throw new Error(await parseApiErrorMessage(r, "Erro ao consultar saldo"));
    return r.json();
  },
  historico: async () => {
    const r = await fetchWithAuth("/saidas-produtos/historico");
    if (!r.ok)
      throw new Error(
        await parseApiErrorMessage(r, "Erro ao buscar histórico de saídas"),
      );
    return r.json();
  },
  registrar: async (dados) => {
    const r = await fetchWithAuth("/saidas-produtos", {
      method: "POST",
      body: JSON.stringify(dados),
    });
    if (!r.ok)
      throw new Error(await parseApiErrorMessage(r, "Erro ao registrar saída"));
    return r.json();
  },
};
const perdaAvariaService = {
  produtos: async (busca = "") => {
    const r = await fetchWithAuth(
      `/perdas-avarias/produtos?${new URLSearchParams({ busca })}`,
    );
    if (!r.ok)
      throw new Error(await parseApiErrorMessage(r, "Erro ao buscar produtos"));
    return r.json();
  },
  saldo: async (id) => {
    const r = await fetchWithAuth(`/perdas-avarias/${id}/saldo`);
    if (!r.ok)
      throw new Error(await parseApiErrorMessage(r, "Erro ao consultar saldo"));
    return r.json();
  },
  historico: async () => {
    const r = await fetchWithAuth("/perdas-avarias/historico");
    if (!r.ok)
      throw new Error(
        await parseApiErrorMessage(r, "Erro ao consultar histórico"),
      );
    return r.json();
  },
  registrar: async (dados) => {
    const r = await fetchWithAuth("/perdas-avarias", {
      method: "POST",
      body: JSON.stringify(dados),
    });
    if (!r.ok)
      throw new Error(
        await parseApiErrorMessage(r, "Erro ao registrar perda ou avaria"),
      );
    return r.json();
  },
};
const saidaEstoqueService = {
  produtos: async (busca = "") => {
    const r = await fetchWithAuth(
      `/saidas-estoque/produtos?${new URLSearchParams({ busca })}`,
    );
    if (!r.ok)
      throw new Error(await parseApiErrorMessage(r, "Erro ao buscar produtos"));
    return r.json();
  },
  saldo: async (id) => {
    const r = await fetchWithAuth(`/saidas-estoque/${id}/saldo`);
    if (!r.ok)
      throw new Error(await parseApiErrorMessage(r, "Erro ao consultar saldo"));
    return r.json();
  },
  historico: async () => {
    const r = await fetchWithAuth("/saidas-estoque/historico");
    if (!r.ok)
      throw new Error(
        await parseApiErrorMessage(r, "Erro ao buscar histórico"),
      );
    return r.json();
  },
  registrar: async (dados) => {
    const r = await fetchWithAuth("/saidas-estoque", {
      method: "POST",
      body: JSON.stringify(dados),
    });
    if (!r.ok)
      throw new Error(await parseApiErrorMessage(r, "Erro ao registrar saída"));
    return r.json();
  },
};
const entradaNotaService = {
  listar: async () => {
    const r = await fetchWithAuth("/entradas-notas");
    if (!r.ok)
      throw new Error(
        await parseApiErrorMessage(r, "Erro ao listar notas fiscais"),
      );
    return r.json();
  },
  detalhe: async (id) => {
    const r = await fetchWithAuth(`/entradas-notas/${id}`);
    if (!r.ok)
      throw new Error(
        await parseApiErrorMessage(r, "Erro ao carregar nota fiscal"),
      );
    return r.json();
  },
  excluir: async (id) => {
    const r = await fetchWithAuth(`/entradas-notas/${id}`, {
      method: "DELETE",
    });
    if (!r.ok)
      throw new Error(
        await parseApiErrorMessage(r, "Erro ao excluir nota fiscal"),
      );
  },
  importar: async (arquivo) => {
    const f = new FormData();
    f.append("arquivo", arquivo);
    const r = await fetchWithAuth("/entradas-notas/importar", {
      method: "POST",
      body: f,
    });
    if (!r.ok)
      throw new Error(
        await parseApiErrorMessage(r, "Erro ao importar nota fiscal"),
      );
    return r.json();
  },
  associarFornecedor: async (id, fornecedorId) => {
    const r = await fetchWithAuth(`/entradas-notas/${id}/fornecedor`, {
      method: "PATCH",
      body: JSON.stringify({ fornecedorId }),
    });
    if (!r.ok)
      throw new Error(
        await parseApiErrorMessage(r, "Erro ao associar fornecedor"),
      );
    return r.json();
  },
  associarProduto: async (id, itemId, produtoId, quantidadeEsperada) => {
    const r = await fetchWithAuth(
      `/entradas-notas/${id}/itens/${itemId}/produto`,
      {
        method: "PATCH",
        body: JSON.stringify({ produtoId, quantidadeEsperada }),
      },
    );
    if (!r.ok)
      throw new Error(
        await parseApiErrorMessage(r, "Erro ao associar produto"),
      );
    return r.json();
  },
  sugestoesProdutos: async (id, itemId) => {
    const r = await fetchWithAuth(
      `/entradas-notas/${id}/itens/${itemId}/sugestoes-produtos`,
    );
    if (!r.ok)
      throw new Error(
        await parseApiErrorMessage(r, "Erro ao buscar sugestões de produtos"),
      );
    return r.json();
  },
  sugestoesProdutosDaNota: async (id) => {
    const r = await fetchWithAuth(
      `/entradas-notas/${id}/itens/sugestoes-produtos`,
    );
    if (!r.ok)
      throw new Error(
        await parseApiErrorMessage(r, "Erro ao buscar sugestões de produtos"),
      );
    return r.json();
  },
  associarProdutosEmLote: async (id, dados) => {
    const r = await fetchWithAuth(
      `/entradas-notas/${id}/itens/produtos-em-lote`,
      { method: "POST", body: JSON.stringify(dados) },
    );
    if (!r.ok)
      throw new Error(
        await parseApiErrorMessage(r, "Erro ao processar associações em lote"),
      );
    return r.json();
  },
  cadastrarProduto: async (id, itemId) => {
    const r = await fetchWithAuth(
      `/entradas-notas/${id}/itens/${itemId}/produto/cadastrar`,
      { method: "POST" },
    );
    if (!r.ok)
      throw new Error(
        await parseApiErrorMessage(r, "Erro ao cadastrar produto"),
      );
    return r.json();
  },
  conferir: async (id, itemId, dados) => {
    const r = await fetchWithAuth(
      `/entradas-notas/${id}/itens/${itemId}/conferencia`,
      { method: "PUT", body: JSON.stringify(dados) },
    );
    if (!r.ok)
      throw new Error(
        await parseApiErrorMessage(r, "Erro ao salvar conferência"),
      );
    return r.json();
  },
  receber: async (id, dados) => {
    const r = await fetchWithAuth(`/entradas-notas/${id}/receber`, {
      method: "POST",
      body: JSON.stringify(dados),
    });
    if (!r.ok)
      throw new Error(
        await parseApiErrorMessage(r, "Erro ao concluir recebimento fiscal"),
      );
    return r.json();
  },
  finalizarConferencia: async (id, dados) => {
    const r = await fetchWithAuth(
      `/entradas-notas/${id}/conferencia/finalizar`,
      { method: "POST", body: JSON.stringify(dados) },
    );
    if (!r.ok)
      throw new Error(
        await parseApiErrorMessage(r, "Erro ao finalizar conferência"),
      );
    return r.json();
  },
  reabrirConferencia: async (id, dados) => {
    const r = await fetchWithAuth(`/entradas-notas/${id}/conferencia/reabrir`, {
      method: "POST",
      body: JSON.stringify(dados),
    });
    if (!r.ok)
      throw new Error(
        await parseApiErrorMessage(r, "Erro ao reabrir conferência"),
      );
    return r.json();
  },
};
const relatorioEstoqueService = {
  movimentacoes: async (params = {}) => {
    const r = await fetchWithAuth(
      `/relatorios-estoque/movimentacoes?${new URLSearchParams(params)}`,
    );
    if (!r.ok)
      throw new Error(
        await parseApiErrorMessage(r, "Erro ao consultar movimentações"),
      );
    return r.json();
  },
  consumo: async (params = {}) => {
    const r = await fetchWithAuth(
      `/relatorios-estoque/consumo?${new URLSearchParams(params)}`,
    );
    if (!r.ok)
      throw new Error(
        await parseApiErrorMessage(r, "Erro ao consultar consumo"),
      );
    return r.json();
  },
  divergencias: async (params = {}) => {
    const r = await fetchWithAuth(
      `/relatorios-estoque/divergencias?${new URLSearchParams(params)}`,
    );
    if (!r.ok)
      throw new Error(
        await parseApiErrorMessage(r, "Erro ao consultar divergências"),
      );
    return r.json();
  },
};
const inteligenciaEstoqueService = {
  listar: async (params = {}) => {
    const r = await fetchWithAuth(
      `/inteligencia-estoque/produtos?${new URLSearchParams(params)}`,
    );
    if (!r.ok)
      throw new Error(
        await parseApiErrorMessage(
          r,
          "Erro ao consultar inteligência de estoque",
        ),
      );
    return r.json();
  },
  detalhe: async (id) => {
    const r = await fetchWithAuth(`/inteligencia-estoque/produtos/${id}`);
    if (!r.ok)
      throw new Error(
        await parseApiErrorMessage(r, "Erro ao carregar produto"),
      );
    return r.json();
  },
  salvarPonto: async (id, d) => {
    const r = await fetchWithAuth(
      `/inteligencia-estoque/produtos/${id}/ponto-reposicao`,
      { method: "PUT", body: JSON.stringify(d) },
    );
    if (!r.ok)
      throw new Error(
        await parseApiErrorMessage(r, "Erro ao salvar ponto de reposição"),
      );
    return r.json();
  },
  removerPonto: async (id, d) => {
    const r = await fetchWithAuth(
      `/inteligencia-estoque/produtos/${id}/ponto-reposicao`,
      { method: "DELETE", body: JSON.stringify(d) },
    );
    if (!r.ok)
      throw new Error(
        await parseApiErrorMessage(r, "Erro ao remover ponto de reposição"),
      );
  },
  adicionarLista: async (d) => {
    const r = await fetchWithAuth("/inteligencia-estoque/adicionar-lista", {
      method: "POST",
      body: JSON.stringify(d),
    });
    if (!r.ok)
      throw new Error(
        await parseApiErrorMessage(r, "Erro ao adicionar à lista"),
      );
    return r.json();
  },
};

// ==================== PONTO SERVICE ====================
const pontoRequest = async (url, mensagemPadrao, options = {}) => {
  const response = await fetchWithAuth(url, options);
  if (!response.ok) {
    throw new Error(await parseApiErrorMessage(response, mensagemPadrao));
  }
  if (response.status === 204) {
    return null;
  }
  return response.json();
};

const pontoService = {
  // Colaboradora
  meuPonto: (competencia) =>
    pontoRequest(
      `/ponto/meu?competencia=${encodeURIComponent(competencia || "")}`,
      "Erro ao carregar o ponto",
    ),
  // Batida manual: horário "HH:mm" de hoje informado pela colaboradora.
  registrar: (horario) =>
    pontoRequest("/ponto/registrar", "Erro ao registrar o ponto", {
      method: "POST",
      body: JSON.stringify({ horario }),
    }),

  // Solicitações de ajuste
  solicitarAjuste: (dados) =>
    pontoRequest("/ponto/solicitacoes", "Erro ao enviar a solicitação", {
      method: "POST",
      body: JSON.stringify(dados),
    }),
  listarSolicitacoes: (status) =>
    pontoRequest(
      `/ponto/solicitacoes?status=${encodeURIComponent(status || "PENDENTE")}`,
      "Erro ao carregar solicitações",
    ),
  contarSolicitacoesPendentes: () =>
    pontoRequest(
      "/ponto/solicitacoes/pendentes/quantidade",
      "Erro ao contar solicitações",
    ),
  aprovarSolicitacao: (id, resposta) =>
    pontoRequest(
      `/ponto/solicitacoes/${id}/aprovar`,
      "Erro ao aprovar a solicitação",
      { method: "POST", body: JSON.stringify({ resposta: resposta || null }) },
    ),
  negarSolicitacao: (id, resposta) =>
    pontoRequest(
      `/ponto/solicitacoes/${id}/negar`,
      "Erro ao negar a solicitação",
      { method: "POST", body: JSON.stringify({ resposta: resposta || null }) },
    ),

  // Colaboradoras e jornada
  listarColaboradoras: () =>
    pontoRequest("/ponto/colaboradoras", "Erro ao carregar colaboradoras"),
  salvarJornada: (usuarioId, jornada) =>
    pontoRequest(
      `/ponto/colaboradoras/${usuarioId}/jornada`,
      "Erro ao salvar a jornada",
      { method: "PUT", body: JSON.stringify(jornada) },
    ),

  // Dias fechados (vêm do Cadastro de impedimentos)
  listarDiasFechados: (ano) =>
    pontoRequest(
      `/ponto/dias-fechados?ano=${ano}`,
      "Erro ao carregar os dias fechados",
    ),
  importarFeriados: (ano) =>
    pontoRequest(
      `/ponto/dias-fechados/importar-feriados?ano=${ano}`,
      "Erro ao importar feriados",
      { method: "POST" },
    ),

  // Afastamentos
  listarTiposAfastamento: () =>
    pontoRequest(
      "/ponto/afastamentos/tipos",
      "Erro ao carregar tipos de afastamento",
    ),
  listarAfastamentos: (ano, usuarioId) =>
    pontoRequest(
      `/ponto/afastamentos?ano=${ano}${usuarioId ? `&usuarioId=${usuarioId}` : ""}`,
      "Erro ao carregar afastamentos",
    ),
  cadastrarAfastamento: (afastamento) =>
    pontoRequest("/ponto/afastamentos", "Erro ao cadastrar afastamento", {
      method: "POST",
      body: JSON.stringify(afastamento),
    }),
  excluirAfastamento: (id) =>
    pontoRequest(`/ponto/afastamentos/${id}`, "Erro ao excluir afastamento", {
      method: "DELETE",
    }),

  // Marcações
  listarMarcacoes: (usuarioId, competencia) =>
    pontoRequest(
      `/ponto/marcacoes?usuarioId=${usuarioId}&competencia=${competencia}`,
      "Erro ao carregar marcações",
    ),
  incluirMarcacao: (dados) =>
    pontoRequest("/ponto/marcacoes", "Erro ao incluir marcação", {
      method: "POST",
      body: JSON.stringify(dados),
    }),
  ajustarMarcacao: (id, dados) =>
    pontoRequest(`/ponto/marcacoes/${id}`, "Erro ao ajustar marcação", {
      method: "PUT",
      body: JSON.stringify(dados),
    }),
  cancelarMarcacao: (id, justificativa) =>
    pontoRequest(
      `/ponto/marcacoes/${id}/cancelar`,
      "Erro ao remover marcação",
      { method: "POST", body: JSON.stringify({ justificativa }) },
    ),

  // Cartão ponto
  listarCartoes: (competencia, usuarioId) =>
    pontoRequest(
      `/ponto/cartoes?competencia=${competencia}${usuarioId ? `&usuarioId=${usuarioId}` : ""}`,
      "Erro ao carregar o cartão ponto",
    ),
  gerarPdf: async (competencia, usuarioId) => {
    const response = await fetchWithAuth(
      `/ponto/cartoes/pdf?competencia=${competencia}${usuarioId ? `&usuarioId=${usuarioId}` : ""}`,
    );
    if (!response.ok) {
      throw new Error(
        await parseApiErrorMessage(response, "Erro ao gerar o PDF do cartão"),
      );
    }
    return response.blob();
  },
};

export {
  authService,
  caixaService,
  caixaGeralService,
  apuracaoMensalFundosService,
  dashboardDespesasService,
  dashboardFinanceiroService,
  comparativoFinanceiroService,
  tipoCaixaService,
  configuracaoIntegracaoSisodontoService,
  agenteSisodontoService,
  fundoReservaService,
  movimentoFundoReservaService,
  especialidadeService,
  folhaPagamentoService,
  formaPagamentoService,
  profissionalService,
  solicitacaoReaberturaCaixaService,
  solicitacaoAberturaCaixaService,
  usuarioService,
  impedimentoService,
  datasComImpedimentoService,
  grupoService,
  organizacaoService,
  subGrupoService,
  despesaService,
  maquinaCartaoService,
  taxaMaquinaCartaoService,
  socioService,
  contaBancariaService,
  contaBancariaValorService,
  fornecedorService,
  unidadeControleService,
  produtoService,
  pedidoCompraService,
  entradaProdutoService,
  entradaManualService,
  saidaProdutoService,
  perdaAvariaService,
  saidaEstoqueService,
  entradaNotaService,
  relatorioEstoqueService,
  inteligenciaEstoqueService,
  pontoService,
};
